import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Grid, IconButton, Typography, Drawer } from "@mui/material";
import { Menu } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { fetchContents, fetchContentById, clearContentError } from "../features/content/contentSlice";
import { fetchProgress, toggleProgress } from "../features/progress/progressSlice";
import LeftSidebar from "../components/body/LeftSidebar";
import MiddleContent from "../components/body/MiddleContent";
import LessonUtilityPanel from "../components/body/LessonUtilityPanel";
import ScrollTopButton from "../components/body/ScrollTopButton";
import PreviousNextNavigation from "../components/body/PreviousNextNavigation";
import { useParams } from "react-router-dom";
import { setSubject } from "../features/subject/subjectSlice";
import buildHierarchy from "../components/body/buildHirerchy";
import { useNavigate } from "react-router-dom";
import { FormatListBulleted } from "@mui/icons-material";

export default function BodyContent() {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
    const subject = useSelector((state) => state.subject?.current || "");
    const { user } = useSelector((state) => state.auth);
    const { subjectParam, contentId } = useParams();
    const navigate = useNavigate();

    const normalizedSubjectParam = useMemo(() => {
        if (!subjectParam) return subjectParam;
        return subjectParam.toLowerCase() === "agenticai" ? "agentic-ai" : subjectParam;
    }, [subjectParam]);

    const { contents = [], selectedContent: reduxSelectedContent, loading, error } = useSelector((state) => state.content);
    const { progressMap = {} } = useSelector((state) => state.progress);
    const [openMain, setOpenMain] = useState({});
    const [selectedContent, setSelectedContent] = useState(null);

    // Filter and sort contents for the current subject to enable navigation
    const subjectContents = useMemo(() => {
        if (!contents || !subject) return [];
        return contents
            .filter((c) => c.subject?.toLowerCase() === subject?.toLowerCase())
            .sort((a, b) => {
                // Sort by content_id if available (handles "1.1", "1.2", "1.10" correctly)
                const idA = a.content_id || "";
                const idB = b.content_id || "";
                if (idA && idB) {
                    return idA.localeCompare(idB, undefined, { numeric: true, sensitivity: 'base' });
                }
                return (a.id || 0) - (b.id || 0);
            });
    }, [contents, subject]);

    // sidebar visibility - default closed on mobile/tablet to avoid obstructing the viewport
    const [leftOpen, setLeftOpen] = useState(false);
    const [rightOpen, setRightOpen] = useState(false);
    const [focusMode, setFocusMode] = useState(false);
    // sidebar widths
    const [leftWidth, setLeftWidth] = useState(350);
    const [rightWidth, setRightWidth] = useState(300);

    const [showScrollTop, setShowScrollTop] = useState(false);
    const [scrolledDown, setScrolledDown] = useState(false);
    const panelRef = useRef(null);

    // Set responsive drawer default states based on viewport size on mount / resize
    useEffect(() => {
        if (isMobile) {
            setLeftOpen(false);
            setRightOpen(false);
        } else if (isTablet) {
            setLeftOpen(true);
            setRightOpen(false);
        } else {
            setLeftOpen(true);
            setRightOpen(true);
        }
    }, [isMobile, isTablet]);

    // update subject when URL changes - this ensures Redux state matches URL
    useEffect(() => {
        if (normalizedSubjectParam && normalizedSubjectParam !== "undefined" && normalizedSubjectParam !== subject) {
            dispatch(setSubject(normalizedSubjectParam));
            dispatch(clearContentError());
        }
    }, [normalizedSubjectParam, dispatch, subject]);

    // fetch contents
    useEffect(() => {
        // Prevent fetching if subject state doesn't match URL param yet (avoids fetching default 'fastapi' content first)
        if (normalizedSubjectParam && subject !== normalizedSubjectParam) {
            return;
        }

        if (subject && subject !== "undefined") {
            dispatch(fetchContents(subject));
        }
    }, [subject, dispatch, normalizedSubjectParam, user]);

    // fetch progress
    useEffect(() => {
        if (subject && subject !== "undefined" && user) {
            dispatch(fetchProgress(subject));
        }
    }, [subject, user, dispatch]);

    // group contents
    const hierarchicalContent = useMemo(() => {
        const roots = buildHierarchy(subjectContents || []);
        return roots.map(root => {
            if (root.children && root.children.length > 0) {
                return {
                    ...root,
                    children: [
                        ...root.children,
                        {
                            content_id: `${root.content_id}.quiz`,
                            title: `📝 Module ${root.content_id} Quiz`,
                            subject: root.subject,
                            isQuiz: true
                        }
                    ]
                };
            }
            return root;
        });
    }, [subjectContents]);

    // Flatten hierarchical content into a single sequential list for navigation
    const flatNavigableItems = useMemo(() => {
        const flat = [];
        const traverse = (items) => {
            items.forEach((item) => {
                if (item.children && item.children.length > 0) {
                    traverse(item.children);
                } else {
                    flat.push(item);
                }
            });
        };
        traverse(hierarchicalContent);
        return flat;
    }, [hierarchicalContent]);

    // Track which content IDs have been fetched to prevent infinite loops
    const fetchedContentIdsRef = useRef(new Set());
    const lastContentIdRef = useRef(null);

    // Store last selected content per subject to avoid auto-selecting when switching subjects
    const lastSelectedBySubjectRef = useRef(new Map());

    // When subject changes and no contentId in URL, navigate to preserve contentId or select first content
    const prevSubjectRef = useRef(subject);
    useEffect(() => {
        if (subject && subject !== "undefined" && flatNavigableItems.length > 0 && !contentId) {
            const subjectChanged = prevSubjectRef.current !== subject;
            prevSubjectRef.current = subject;

            if (subjectChanged || (normalizedSubjectParam === subject && !contentId)) {
                const lastSelected = lastSelectedBySubjectRef.current.get(subject);
                if (lastSelected) {
                    const found = flatNavigableItems.find((c) => c.content_id === lastSelected || c.id === lastSelected);
                    if (found) {
                        const urlContentId = found.content_id || found.title;
                        navigate(`/${subject}/${urlContentId}`, { replace: true });
                        return;
                    }
                }

                if (flatNavigableItems.length > 0) {
                    const firstContent = flatNavigableItems[0];
                    const urlContentId = firstContent.content_id || firstContent.title;
                    navigate(`/${subject}/${urlContentId}`, { replace: true });
                }
            }
        }
    }, [subject, flatNavigableItems, contentId, navigate, normalizedSubjectParam]);

    // Sync active content from URL contentId parameter
    useEffect(() => {
        if (!subject || subject === "undefined" || (subjectContents.length === 0 && flatNavigableItems.length === 0)) {
            setSelectedContent(null);
            lastContentIdRef.current = null;
            fetchedContentIdsRef.current.clear();
            return;
        }

        let found = null;

        if (contentId) {
            // 1. Try to find match in flatNavigableItems (matches lessons AND quizzes!)
            found = flatNavigableItems.find((c) => c.content_id === contentId);

            // 2. Try to find match by title
            if (!found) {
                found = flatNavigableItems.find((c) => c.title === contentId || c.title?.toLowerCase() === contentId.toLowerCase());
            }

            // 3. Fallback to subjectContents
            if (!found) {
                found = subjectContents.find((c) => c.content_id === contentId || c.title === contentId);
            }

            // 4. Handle clicking a parent module ID (e.g. "2") by choosing its first child lesson
            if (!found && hierarchicalContent.length > 0) {
                const parentModule = hierarchicalContent.find((m) => m.content_id === contentId);
                if (parentModule && parentModule.children && parentModule.children.length > 0) {
                    found = parentModule.children[0];
                }
            }

            // 5. If contentId is invalid for current subject, redirect to first content item
            if (!found && flatNavigableItems.length > 0) {
                console.log(`ContentId "${contentId}" not found in subject "${subject}". Navigating to first content.`);
                const firstContent = flatNavigableItems[0];
                const urlContentId = firstContent.content_id || firstContent.title;
                navigate(`/${subject}/${urlContentId}`, { replace: true });
                return;
            }
        } else {
            // If no contentId in URL, use last selected or first available
            const lastSelected = lastSelectedBySubjectRef.current.get(subject);
            if (lastSelected) {
                found = flatNavigableItems.find((c) => c.content_id === lastSelected || c.id === lastSelected);
            }
            if (!found && flatNavigableItems.length > 0) {
                found = flatNavigableItems[0];
            }
        }

        if (!found) return;

        const currentContentId = found.content_id || found.id;

        // Only update state when active item content_id actually changes
        if (lastContentIdRef.current !== currentContentId) {
            lastContentIdRef.current = currentContentId;
            setSelectedContent(found);
            lastSelectedBySubjectRef.current.set(subject, currentContentId);
        }

        // Fetch full lesson content text if needed
        if (found.id) {
            const needsFetch = !found.content_text && !fetchedContentIdsRef.current.has(found.id);
            const reduxMismatch = reduxSelectedContent && reduxSelectedContent.id === found.id && !reduxSelectedContent.content_text;

            if (needsFetch || reduxMismatch) {
                if (!fetchedContentIdsRef.current.has(found.id)) {
                    fetchedContentIdsRef.current.add(found.id);
                    dispatch(fetchContentById(found.id)).catch((err) => {
                        console.error("Failed to fetch full content:", err);
                        fetchedContentIdsRef.current.delete(found.id);
                    });
                }
            }
        }

        // Automatically expand the parent module in sidebar
        const mainId = found.content_id?.split(".")[0];
        if (mainId) {
            setOpenMain((prev) => {
                if (prev[mainId] !== true) {
                    return { ...prev, [mainId]: true };
                }
                return prev;
            });
        }

        // Smooth scroll to top of main content panel
        if (panelRef.current) {
            panelRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [subjectContents, flatNavigableItems, hierarchicalContent, contentId, dispatch, subject, reduxSelectedContent, navigate]);

    const handleMainClick = (mainId) =>
        setOpenMain((prev) => ({ ...prev, [mainId]: !prev[mainId] }));

    const handleSelectContent = (targetItem) => {
        if (!targetItem) return;

        // If a parent module header with children was clicked, select its first child lesson
        let item = targetItem;
        if (item.children && item.children.length > 0) {
            item = item.children[0];
        }

        // Update local state immediately for responsive UI
        setSelectedContent(item);
        const itemContentId = item.content_id || item.title;

        if (itemContentId) {
            lastContentIdRef.current = itemContentId;
            if (subject) {
                lastSelectedBySubjectRef.current.set(subject, itemContentId);
            }
        }

        // Always fetch full content when a lesson item is selected
        if (item && item.id) {
            if (!item.content_text && !fetchedContentIdsRef.current.has(item.id)) {
                fetchedContentIdsRef.current.add(item.id);
                dispatch(fetchContentById(item.id)).catch((err) => {
                    console.error("Failed to fetch full content:", err);
                    fetchedContentIdsRef.current.delete(item.id);
                });
            } else if (item.content_text) {
                dispatch(fetchContentById(item.id)).catch(() => {});
            }
        }

        // Navigate using content_id for proper URL matching
        const urlContentId = item.content_id || item.title;
        navigate(`/${subject}/${urlContentId}`);
    };

    // Use Redux selectedContent (with content_text) if available and matches, otherwise use local state
    const displayContent = useMemo(() => {
        if (selectedContent) {
            if (selectedContent.isQuiz) {
                return selectedContent;
            }
            if (reduxSelectedContent && reduxSelectedContent.id === selectedContent.id) {
                return { ...selectedContent, ...reduxSelectedContent };
            }
            if (selectedContent.content_text) {
                return selectedContent;
            }
            return selectedContent;
        }
        return null;
    }, [reduxSelectedContent, selectedContent]);

    // Find previous and next items based on current active location in the flattened list
    const { prevItem, nextItem } = useMemo(() => {
        if (!displayContent) return { prevItem: null, nextItem: null };
        const index = flatNavigableItems.findIndex(item => item.content_id === displayContent.content_id);
        return {
            prevItem: index > 0 ? flatNavigableItems[index - 1] : null,
            nextItem: index >= 0 && index < flatNavigableItems.length - 1 ? flatNavigableItems[index + 1] : null,
        };
    }, [displayContent, flatNavigableItems]);

    // Handler to check/uncheck lesson progress completion status
    const handleToggleComplete = () => {
        if (!user) {
            navigate("/signin", { state: { from: { pathname: window.location.pathname } } });
            return;
        }
        if (displayContent && subject) {
            const isCompleted = !!progressMap[displayContent.content_id];
            dispatch(toggleProgress({
                subject,
                contentId: displayContent.content_id,
                completed: !isCompleted
            }));
        }
    };

    useEffect(() => {
        const container = panelRef.current;
        if (!container) return;

        const onScroll = () => {
            const scrollY = container.scrollTop;
            setShowScrollTop(scrollY > 200);
            setScrolledDown(scrollY > 50);
        };

        container.addEventListener("scroll", onScroll);
        return () => container.removeEventListener("scroll", onScroll);
    }, []);

    // toggle handlers
    const onToggleLeft = () => setLeftOpen((prev) => !prev);
    const onToggleRight = () => setRightOpen((prev) => !prev);
    // resizing logic
    const startResizing = (side, e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startLeft = leftWidth;
        const startRight = rightWidth;

        const onMouseMove = (moveEvent) => {
            const deltaX = moveEvent.clientX - startX;

            if (side === 'left') {
                const newWidth = Math.max(150, Math.min(400, startLeft + deltaX));
                setLeftWidth(newWidth);
            }

            if (side === 'right') {
                const newWidth = Math.max(150, Math.min(400, startRight - deltaX));
                setRightWidth(newWidth);
            }
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };


    const colors = {
        primary: "#e50914",
        secondary: "#6a0dad",
    };

    return (
        <Box
            sx={{
                height: {
                    xs: "calc(100dvh - 56px)",
                    sm: "calc(100vh - 64px)",
                },
                display: "flex",
                flexDirection: "column",
                position: "relative",
                background: isDark
                    ? "linear-gradient(180deg, #0a0a0a 0%, #1a0a1a 50%, #0a0a0a 100%)"
                    : "linear-gradient(180deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)",
                overflow: "hidden",
            }}
        >
            {/* Ambient Background Blobs */}
            <Box
                sx={{
                    position: "absolute",
                    top: "10%",
                    left: "5%",
                    width: "35vw",
                    height: "35vw",
                    background: `radial-gradient(circle, ${colors.primary}18 0%, transparent 70%)`,
                    filter: "blur(80px)",
                    zIndex: 0,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    bottom: "10%",
                    right: "5%",
                    width: "35vw",
                    height: "35vw",
                    background: `radial-gradient(circle, ${colors.secondary}18 0%, transparent 70%)`,
                    filter: "blur(80px)",
                    zIndex: 0,
                    pointerEvents: "none",
                }}
            />
            {/* Animated Grid Background */}
            <Box
                className="coder-bg"
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    pointerEvents: "none",
                }}
            />
            {/* Mobile Top Bar */}
            {isMobile && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: isDark ? "rgba(10, 10, 10, 0.45)" : "rgba(255, 255, 255, 0.45)",
                        backdropFilter: "blur(12px)",
                        color: theme.palette.text.primary,
                        zIndex: 2,
                    }}
                >
                    <IconButton onClick={onToggleLeft} edge="start" color="inherit" aria-label="Course curriculum menu">
                        <Menu />
                    </IconButton>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        📖 {subjectParam || subject}
                    </Typography>
                    <IconButton onClick={onToggleRight} edge="end" color="inherit" aria-label="Lesson outline menu">
                        <FormatListBulleted />
                    </IconButton>
                </Box>
            )}

            <Grid container sx={{ flexGrow: 1, overflow: "hidden" }}>
                {/* LEFT SIDEBAR (Desktop / Tablet) */}
                {!isMobile && (
                    <Box
                        sx={{
                            width: focusMode ? 0 : (leftOpen ? leftWidth : 60),
                            borderRight: focusMode ? "none" : `1px solid ${theme.palette.divider}`,
                            height: "100%",
                            overflow: "hidden",
                            transition: "all 0.3s ease",
                            position: "relative",
                            backgroundColor: isDark ? "rgba(10, 10, 10, 0.45)" : "rgba(255, 255, 255, 0.45)",
                            backdropFilter: "blur(12px)",
                            zIndex: 1,
                            visibility: focusMode ? "hidden" : "visible",
                        }}
                    >
                        <LeftSidebar
                            hierarchicalContent={hierarchicalContent}
                            openMain={openMain}
                            handleMainClick={handleMainClick}
                            handleSelectContent={handleSelectContent}
                            selectedContent={displayContent}
                            leftOpen={leftOpen}
                            onToggleLeft={onToggleLeft}
                            progressMap={progressMap}
                        />
                        {leftOpen && !focusMode && (
                            <Box
                                onMouseDown={(e) => startResizing("left", e)}
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    width: "5px",
                                    height: "100%",
                                    cursor: "col-resize",
                                    "&:hover": { backgroundColor: theme.palette.action.hover },
                                }}
                            />
                        )}
                    </Box>
                )}

                {/* MIDDLE CONTENT */}
                <Box
                    ref={panelRef}
                    sx={{
                        flex: 1,
                        height: "100%",
                        overflowY: "auto",
                        WebkitOverflowScrolling: "touch",
                        overscrollBehaviorY: "contain",
                        touchAction: "pan-y",
                        p: { xs: 1.5, sm: 3 },
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        backgroundColor: "transparent",
                        color: theme.palette.text.primary,
                        transition: "background-color 0.3s ease, color 0.3s ease",
                        position: "relative",
                        zIndex: 1,
                        scrollbarWidth: "thin",
                        scrollbarColor: isDark
                            ? "rgba(255, 255, 255, 0.2) transparent"
                            : "rgba(0, 0, 0, 0.2) transparent",
                        "&::-webkit-scrollbar": {
                            width: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: isDark
                                ? "rgba(255, 255, 255, 0.2)"
                                : "rgba(0, 0, 0, 0.2)",
                            borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: isDark
                                ? "rgba(255, 255, 255, 0.35)"
                                : "rgba(0, 0, 0, 0.35)",
                        },
                    }}
                >
                    <Box sx={{ maxWidth: "850px", width: "100%", mx: "auto", flex: 1, display: "flex", flexDirection: "column" }}>
                        <MiddleContent
                            onContentUpdate={() => {
                                if (selectedContent?.id) {
                                    dispatch(fetchContentById(selectedContent.id)).catch(console.error);
                                }
                            }}
                            selectedContent={displayContent}
                            scrolledDown={scrolledDown}
                            loading={loading}
                            error={error}
                            onRetry={() => {
                                if (selectedContent?.id) {
                                    dispatch(fetchContentById(selectedContent.id)).catch(console.error);
                                } else if (subject) {
                                    dispatch(fetchContents(subject));
                                }
                            }}
                            focusMode={focusMode}
                            onToggleFocusMode={() => setFocusMode(!focusMode)}
                        />

                        {/* PREVIOUS & NEXT NAVIGATION BUTTONS */}
                        {displayContent && (
                            <PreviousNextNavigation
                                prevItem={prevItem}
                                nextItem={nextItem}
                                completed={!!progressMap[displayContent.content_id]}
                                onNavigate={handleSelectContent}
                                onToggleComplete={handleToggleComplete}
                            />
                        )}
                    </Box>

                    <ScrollTopButton
                        show={showScrollTop}
                        onClick={() =>
                            panelRef.current.scrollTo({ top: 0, behavior: "smooth" })
                        }
                    />
                </Box>

                {/* RIGHT UTILITY PANEL (Desktop / Tablet) */}
                {!isMobile && (
                    <Box
                        sx={{
                            width: focusMode ? 0 : (rightOpen ? rightWidth : 60),
                            borderLeft: focusMode ? "none" : `1px solid ${theme.palette.divider}`,
                            height: "100%",
                            overflow: "hidden",
                            transition: "all 0.3s ease",
                            position: "relative",
                            backgroundColor: isDark ? "rgba(10, 10, 10, 0.45)" : "rgba(255, 255, 255, 0.45)",
                            backdropFilter: "blur(12px)",
                            zIndex: 1,
                            visibility: focusMode ? "hidden" : "visible",
                        }}
                    >
                        <LessonUtilityPanel
                            selectedContent={displayContent}
                            rightOpen={rightOpen}
                            onToggleRight={onToggleRight}
                            panelRef={panelRef}
                        />
                        {rightOpen && (
                            <Box
                                onMouseDown={(e) => startResizing("right", e)}
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "5px",
                                    height: "100%",
                                    cursor: "col-resize",
                                    "&:hover": { backgroundColor: theme.palette.action.hover },
                                }}
                            />
                        )}
                    </Box>
                )}
            </Grid>

            {/* Mobile Drawers */}
            {isMobile && (
                <>
                    {/* Left Course Navigation Drawer */}
                    <Drawer
                        anchor="left"
                        open={leftOpen}
                        onClose={() => setLeftOpen(false)}
                        ModalProps={{
                            keepMounted: false,
                        }}
                        PaperProps={{
                            sx: {
                                width: { xs: "85vw", sm: "320px" },
                                maxWidth: "340px",
                                height: "100%",
                                overflowY: "auto",
                                backgroundColor: isDark ? "#121212" : "#ffffff",
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        <LeftSidebar
                            hierarchicalContent={hierarchicalContent}
                            openMain={openMain}
                            handleMainClick={handleMainClick}
                            handleSelectContent={(item) => {
                                handleSelectContent(item);
                                setLeftOpen(false); // close drawer on selection
                            }}
                            selectedContent={displayContent}
                            leftOpen={true}
                            onToggleLeft={() => setLeftOpen(false)}
                            progressMap={progressMap}
                        />
                    </Drawer>

                    {/* Right Utility/Discussion Drawer */}
                    <Drawer
                        anchor="right"
                        open={rightOpen}
                        onClose={() => setRightOpen(false)}
                        ModalProps={{
                            keepMounted: false,
                        }}
                        PaperProps={{
                            sx: {
                                width: { xs: "85vw", sm: "320px" },
                                maxWidth: "340px",
                                height: "100%",
                                overflowY: "auto",
                                backgroundColor: isDark ? "#121212" : "#ffffff",
                                color: theme.palette.text.primary,
                            },
                        }}
                    >
                        <LessonUtilityPanel
                            selectedContent={displayContent}
                            rightOpen={true}
                            onToggleRight={() => setRightOpen(false)}
                            panelRef={panelRef}
                        />
                    </Drawer>
                </>
            )}
        </Box >
    );
}
